import React, { useEffect, useRef } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import { Toaster } from "sonner";
import api from "@/lib/apiClient";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { ThemeProvider } from "@/context/ThemeContext";
import Landing from "@/pages/Landing";
import Auth from "@/pages/Auth";
import Onboarding from "@/pages/Onboarding";
import Feed from "@/pages/Feed";
import Dashboard from "@/pages/Dashboard";
import InstructorStudio from "@/pages/InstructorStudio";
import Discover from "@/pages/Discover";
import Community from "@/pages/Community";
import Messages from "@/pages/Messages";
import Nutrition from "@/pages/Nutrition";
import FormChecker from "@/pages/FormChecker";
import LiveRoom from "@/pages/LiveRoom";
import PaymentSuccess from "@/pages/PaymentSuccess";
import PaymentCancel from "@/pages/PaymentCancel";
import AppShell from "@/components/AppShell";

function AuthCallback() {
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const done = useRef(false);

  useEffect(() => {
    if (done.current) return;
    done.current = true;
    const hash = window.location.hash;
    const sid = new URLSearchParams(hash.replace("#", "")).get("session_id");
    (async () => {
      try {
        const res = await api.post("/auth/google/session", {}, { headers: { "X-Session-ID": sid } });
        localStorage.setItem("fc_token", res.data.token);
        setUser(res.data.user);
        window.history.replaceState(null, "", "/feed");
        navigate("/feed", { replace: true });
      } catch {
        navigate("/auth", { replace: true });
      }
    })();
  }, [navigate, setUser]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#121212]">
      <div className="text-center">
        <div className="w-12 h-12 border-2 border-[#FF6B6B] border-t-transparent rounded-full animate-spin mx-auto mb-4" />
        <p className="text-[#A0A0A0]">Signing you in…</p>
      </div>
    </div>
  );
}

function Protected({ children }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#121212]">
        <div className="w-12 h-12 border-2 border-[#FF6B6B] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  if (!user) return <Navigate to="/auth" replace />;
  return children;
}

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;

  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/auth" element={<Auth />} />
      <Route path="/onboarding" element={<Protected><Onboarding /></Protected>} />
      <Route path="/live/:bookingId" element={<Protected><LiveRoom /></Protected>} />
      <Route path="/payment/success" element={<Protected><PaymentSuccess /></Protected>} />
      <Route path="/payment/cancel" element={<Protected><PaymentCancel /></Protected>} />
      <Route element={<Protected><AppShell /></Protected>}>
        <Route path="/feed" element={<Feed />} />
        <Route path="/dashboard" element={<Dashboard />} />
        <Route path="/studio" element={<InstructorStudio />} />
        <Route path="/discover" element={<Discover />} />
        <Route path="/community" element={<Community />} />
        <Route path="/messages" element={<Messages />} />
        <Route path="/nutrition" element={<Nutrition />} />
        <Route path="/form-checker" element={<FormChecker />} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App grain">
      <AuthProvider>
        <ThemeProvider>
          <BrowserRouter>
            <AppRouter />
          </BrowserRouter>
          <Toaster theme="dark" position="top-right" toastOptions={{
            style: { background: "#1E1E1E", border: "1px solid #2A2A2A", color: "#FFFFFF" },
          }} />
        </ThemeProvider>
      </AuthProvider>
    </div>
  );
}

export default App;
